/**
 * A balance record is 1 entry on a Wix Payments account ledger, representing 1 financial
 * operation on the account: a payment, a refund, a chargeback, a refund or chargeback
 * reversal, a reserve hold or release, a top-up, a Wix Capital cash advance, a Wix Capital
 * remittance, or an adjustment.
 *
 * Each record belongs to an account and to one of the account's profiles. Reserve, payment,
 * payout, and book transfer details are populated based on the record's type. On every
 * record, `net` equals `amount - fee`. Records are immutable: `amount`, `fee`, and `net`
 * never change after creation.
 *
 * In certain cases a balance record can be virtual: a record that hasn't been persisted but
 * is projected to occur in the future. Virtual records are returned only for the
 * `ESTIMATED_RESERVE_HOLD` and `ESTIMATED_RESERVE_RELEASE` tags and have no `id` and no
 * `appliedDate`.
 */
export interface BalanceRecord {
  /**
   * Balance record ID.
   *
   * Not returned for virtual records produced by `ESTIMATED_RESERVE_HOLD` or
   * `ESTIMATED_RESERVE_RELEASE` tags.
   * @format GUID
   */
  id?: string | null;
  /**
   * ID of the Wix Payments account this balance record belongs to.
   * @format GUID
   */
  accountId?: string;
  /**
   * ID of the Wix Payments account profile this balance record belongs to.
   * @format GUID
   */
  accountProfileId?: string;
  /**
   * Gross amount of the balance record, including fees.
   *
   * Positive for credits to the account, negative for deductions.
   */
  amount?: Money;
  /** Fee that Wix Payments collected on this balance record. */
  fee?: Money;
  /** Net amount of the balance record after subtracting `fee`. */
  net?: Money;
  /** Type of the balance record. Describes what kind of money movement this record represents. */
  recordType?: BalanceRecordTypeWithLiterals;
  /**
   * Current effect of this record on the account balance.
   *
   * The value depends on the record's `recordType`, whether it has a `payoutInfo` or
   * `bookTransferInfo`, and its `payoutAvailabilityDate`. This field is returned on
   * responses only and can't be passed as a filter on list or query requests. To filter by
   * effect on the balance, use `tags` instead.
   */
  recordStatus?: BalanceRecordStatusWithLiterals;
  /**
   * Date and time the balance record was applied to the account balance.
   *
   * Not returned for virtual records.
   */
  appliedDate?: Date | null;
  /**
   * Date the balance record becomes available for payout, in `YYYY-MM-DD` format.
   * @format LOCAL_DATE
   */
  payoutAvailabilityDate?: string | null;
  /** Date and time the balance record was created. */
  createdDate?: Date | null;
  /** Date and time the balance record was last updated. */
  updatedDate?: Date | null;
  /**
   * Information about the payment that produced this balance record.
   *
   * Returned for records related to a payment, refund, or chargeback.
   */
  paymentInfo?: PaymentInfo;
  /**
   * Information about the payout that includes this balance record.
   *
   * Returned only after the record has been included in a payout. The presence of
   * `payoutInfo` (versus `bookTransferInfo`) distinguishes a regular payout from an Instant
   * Access book transfer.
   */
  payoutInfo?: PayoutInfo;
  /**
   * Reserve details for this balance record.
   *
   * Returned for records with `recordType` `RESERVE_HOLD` or `RESERVE_RELEASE`.
   */
  reserveInfo?: ReserveInfo;
  /**
   * Book transfer details for this balance record.
   *
   * Returned for records funded through a Wix Checking book transfer (Instant Access) rather
   * than a payout. Funds backed by a book transfer are immediately available in the
   * merchant's Wix Checking account.
   */
  bookTransferInfo?: BookTransferInfo;
}

export enum PaymentMethodType {
  UNKNOWN = 'UNKNOWN',
  CARD = 'CARD',
  CARD_READER = 'CARD_READER',
  ACH = 'ACH',
  APPLE_PAY = 'APPLE_PAY',
  BOLETO = 'BOLETO',
  GIROPAY = 'GIROPAY',
  IDEAL = 'IDEAL',
  PIX = 'PIX',
  SEPA = 'SEPA',
  SOFORT = 'SOFORT',
  PAY_PAL = 'PAY_PAL',
  TAP_TO_PAY = 'TAP_TO_PAY',
  GOOGLE_PAY = 'GOOGLE_PAY',
  AFFIRM = 'AFFIRM',
  AFTERPAY = 'AFTERPAY',
  KLARNA = 'KLARNA',
  RAZORPAY = 'RAZORPAY',
  ALIPAY = 'ALIPAY',
  BANCONTACT = 'BANCONTACT',
  US_BANK_ACCOUNT = 'US_BANK_ACCOUNT',
  BANKFUL_CRYPTO = 'BANKFUL_CRYPTO',
  BANKFUL = 'BANKFUL',
  TWINT = 'TWINT',
}

/** @enumType */
export type PaymentMethodTypeWithLiterals =
  | PaymentMethodType
  | 'UNKNOWN'
  | 'CARD'
  | 'CARD_READER'
  | 'ACH'
  | 'APPLE_PAY'
  | 'BOLETO'
  | 'GIROPAY'
  | 'IDEAL'
  | 'PIX'
  | 'SEPA'
  | 'SOFORT'
  | 'PAY_PAL'
  | 'TAP_TO_PAY'
  | 'GOOGLE_PAY'
  | 'AFFIRM'
  | 'AFTERPAY'
  | 'KLARNA'
  | 'RAZORPAY'
  | 'ALIPAY'
  | 'BANCONTACT'
  | 'US_BANK_ACCOUNT'
  | 'BANKFUL_CRYPTO'
  | 'BANKFUL'
  | 'TWINT';

export interface PayoutInfo {
  /**
   * ID of the payout that this balance record belongs to.
   * @format GUID
   */
  payoutId?: string;
  /**
   * Date and time the payout was created.
   *
   * Returned only for payouts that have already occurred, not those that are scheduled.
   */
  createdDate?: Date | null;
}

export interface ReserveHistoryItem {
  /**
   * ID of the BackOffice activity log entry that describes this reserve event.
   * @internal
   * @format GUID
   */
  activityId?: string | null;
  /**
   * Date the reserve was scheduled to be released, in `YYYY-MM-DD` format.
   * @format LOCAL_DATE
   */
  releaseDate?: string | null;
  /** Date and time the reserve was created. */
  createdDate?: Date | null;
}

/** How the reserve was created. */
export enum ReserveType {
  /** Reserve that was created manually for a 1-time event. */
  ONE_TIME = 'ONE_TIME',
  /** Reserve that was created automatically by an active reserve policy on the account. */
  ROLLING = 'ROLLING',
}

/** @enumType */
export type ReserveTypeWithLiterals = ReserveType | 'ONE_TIME' | 'ROLLING';

/**
 * Money.
 * Default format to use. Sufficiently compliant with majority of standards: w3c, ISO 4217, ISO 20022, ISO 8583:2003.
 */
export interface Money {
  /**
   * Monetary amount. Decimal string with a period as a decimal separator (e.g., 3.99). Optionally, a single (-), to indicate that the amount is negative.
   * @format DECIMAL_VALUE
   */
  value?: string;
  /**
   * Currency code. Must be valid ISO 4217 currency code (e.g., USD).
   * @format CURRENCY
   */
  currency?: string;
  /** Monetary amount. Decimal string in local format (e.g., 1 000,30). Optionally, a single (-), to indicate that the amount is negative. */
  formattedValue?: string | null;
}

/** Type of financial operation that produced the balance record. */
export enum BalanceRecordType {
  /** Unknown balance record type. */
  UNKNOWN = 'UNKNOWN',
  /** Payment from a buyer for a sale, credited to the merchant's Wix Payments account. */
  CREDIT = 'CREDIT',
  /** Refund issued back to a buyer. */
  REFUND = 'REFUND',
  /** Chargeback initiated by a buyer against a previous payment. */
  CHARGEBACK = 'CHARGEBACK',
  /** Reversal of a previously issued refund. */
  REFUND_REVERSAL = 'REFUND_REVERSAL',
  /** Reversal of a previously issued chargeback. */
  CHARGEBACK_REVERSAL = 'CHARGEBACK_REVERSAL',
  /** Funds withheld from the available balance as part of a reserve. */
  RESERVE_HOLD = 'RESERVE_HOLD',
  /** Funds returned to the available balance from a previously held reserve. */
  RESERVE_RELEASE = 'RESERVE_RELEASE',
  /** Transfer of funds from the merchant's external account into their Wix Payments account. */
  TOPUP = 'TOPUP',
  /** Failure on a previously credited payment, resulting in a deduction from the balance. */
  CREDIT_FAILED = 'CREDIT_FAILED',
  /** Fee charged for processing a chargeback. */
  CHARGEBACK_FEE = 'CHARGEBACK_FEE',
  /** Reversal of a previously charged chargeback fee. */
  CHARGEBACK_FEE_REVERSAL = 'CHARGEBACK_FEE_REVERSAL',
  /** Cash advance disbursed to the merchant from Wix Capital. */
  CAPITAL_CASH_ADVANCE = 'CAPITAL_CASH_ADVANCE',
  /** Automatic remittance taken from sales to repay a Wix Capital cash advance. */
  CAPITAL_AUTOMATIC_REMITTANCE = 'CAPITAL_AUTOMATIC_REMITTANCE',
  /** Adjustment applied to a Wix Capital cash advance balance. */
  CAPITAL_ADJUSTMENT = 'CAPITAL_ADJUSTMENT',
}

/** @enumType */
export type BalanceRecordTypeWithLiterals =
  | BalanceRecordType
  | 'UNKNOWN'
  | 'CREDIT'
  | 'REFUND'
  | 'CHARGEBACK'
  | 'REFUND_REVERSAL'
  | 'CHARGEBACK_REVERSAL'
  | 'RESERVE_HOLD'
  | 'RESERVE_RELEASE'
  | 'TOPUP'
  | 'CREDIT_FAILED'
  | 'CHARGEBACK_FEE'
  | 'CHARGEBACK_FEE_REVERSAL'
  | 'CAPITAL_CASH_ADVANCE'
  | 'CAPITAL_AUTOMATIC_REMITTANCE'
  | 'CAPITAL_ADJUSTMENT';

/**
 * Current effect of a balance record on the account balance.
 *
 * Each record type takes its own path through the values below.
 *
 * Credits (`CREDIT`, `TOPUP`, `REFUND_REVERSAL`, `CHARGEBACK_REVERSAL`,
 * `CHARGEBACK_FEE_REVERSAL`, `CAPITAL_CASH_ADVANCE`) start at `PENDING_CREDIT`, settle to
 * `CREDITED`, and move to `PAID_OUT` once they have been included in a payout.
 *
 * Debits (`REFUND`, `CHARGEBACK`, `CHARGEBACK_FEE`, `CREDIT_FAILED`,
 * `CAPITAL_AUTOMATIC_REMITTANCE`) are deducted from the available balance immediately on
 * creation (`PENDING_DEBIT`), move to `DEBITED` once the operation finalizes, and move to
 * `PAID_OUT` once they have been included in a payout (subtracted from the payout total).
 *
 * A reserve hold (`RESERVE_HOLD`) starts at `RESERVED` while the funds are held back, then
 * moves to `DEDUCTED_FROM_PAYOUT` once the hold has been included in a payout. A reserve
 * release (`RESERVE_RELEASE`) stays at `RESERVED` until its `payoutAvailabilityDate`
 * passes, then moves to `CREDITED`, then `PAID_OUT` like any other credit.
 *
 * Records funded via a Wix Checking book transfer (Instant Access) skip the payout flow:
 * they move directly to `PAID_OUT` because the funds are immediately available.
 *
 * If a payout that included a record is later reversed by the bank, the record's
 * `recordStatus` moves to `PAYOUT_REVERSED` and the record re-enters the available balance.
 */
export enum BalanceRecordStatus {
  /** Unknown balance record status. */
  UNKNOWN = 'UNKNOWN',
  /** Funds from this record are added to the available balance once they settle. They aren't yet part of the available balance. */
  PENDING_CREDIT = 'PENDING_CREDIT',
  /** Funds from this record are part of the available balance. */
  CREDITED = 'CREDITED',
  /**
   * Funds from this record are held in reserve and aren't part of the available balance.
   *
   * Applied to `RESERVE_HOLD` records, and to `RESERVE_RELEASE` records whose `payoutAvailabilityDate` hasn't yet passed.
   */
  RESERVED = 'RESERVED',
  /** Funds from this record have already been deducted from the available balance. The status moves to `DEBITED` once the operation that produced the debit finalizes. */
  PENDING_DEBIT = 'PENDING_DEBIT',
  /** Funds from this record have been deducted from the available balance. */
  DEBITED = 'DEBITED',
  /**
   * The record has been included in a payout (regular payout) or made instantly
   * available via a Wix Checking book transfer (Instant Access). Applies to both credits
   * and debits. The only record type bundled into a payout that ends at a different
   * status is `RESERVE_HOLD` (see `DEDUCTED_FROM_PAYOUT`). Check `payoutInfo` versus
   * `bookTransferInfo` to tell them apart.
   */
  PAID_OUT = 'PAID_OUT',
  /**
   * A `RESERVE_HOLD` record that has been included in a payout. The held amount was
   * subtracted from the payout total instead of being sent to the merchant. Only reserve
   * hold records end at this status; refunds, chargebacks, and other debits in a payout
   * end at `PAID_OUT`.
   */
  DEDUCTED_FROM_PAYOUT = 'DEDUCTED_FROM_PAYOUT',
  /** Funds from this record were in a payout that has since failed and been reversed. The reversal returns the funds to the available balance. */
  PAYOUT_REVERSED = 'PAYOUT_REVERSED',
}

/** @enumType */
export type BalanceRecordStatusWithLiterals =
  | BalanceRecordStatus
  | 'UNKNOWN'
  | 'PENDING_CREDIT'
  | 'CREDITED'
  | 'RESERVED'
  | 'PENDING_DEBIT'
  | 'DEBITED'
  | 'PAID_OUT'
  | 'DEDUCTED_FROM_PAYOUT'
  | 'PAYOUT_REVERSED';

export interface PaymentInfo {
  /**
   * ID of the Wix Payments transaction that produced this balance record.
   * @format GUID
   */
  transactionId?: string;
  /** Date and time the underlying payment was created. */
  createdDate?: Date | null;
  /**
   * External transaction ID supplied by the merchant's order system, if any.
   * @maxLength 100
   */
  externalTransactionId?: string | null;
  /**
   * External order ID supplied by the merchant's order system, if any.
   * @maxLength 100
   */
  externalOrderId?: string | null;
  /**
   * Display-friendly external order ID, suitable for showing in a UI.
   * @maxLength 100
   */
  externalDisplayOrderId?: string | null;
  /**
   * Link to the order in the merchant's external order management system.
   * @maxLength 1000
   */
  externalOrderLink?: string | null;
  /**
   * Customer's name as captured by the payment flow.
   * @maxLength 1000
   */
  customerName?: string | null;
  /**
   * Cardholder's name as captured at payment time.
   * @maxLength 1000
   */
  cardHolderName?: string | null;
  /**
   * Payment method used for the underlying transaction.
   *
   * Deprecated. Use `paymentMethodTypeId` instead.
   * @deprecated Payment method used for the underlying transaction.
   *
   * Deprecated. Use `paymentMethodTypeId` instead.
   * @replacedBy balance_record.payment_info.payment_method_type_id
   * @targetRemovalDate 2027-05-31
   */
  paymentMethodType?: PaymentMethodTypeWithLiterals;
  /**
   * Identifier of the payment method used for the underlying transaction.
   * @internal
   * @readonly
   * @maxLength 100
   */
  paymentMethodTypeId?: string | null;
}

export interface ReserveInfo {
  /** Payout details for the hold portion of the reserve, where funds are removed from the available balance. */
  hold?: PayoutInfo;
  /** Payout details for the release portion of the reserve, where funds are returned to the available balance. */
  release?: PayoutInfo;
  /**
   * History of changes applied to this reserve, ordered by event time.
   * @maxSize 100
   */
  reserveHistoryItems?: ReserveHistoryItem[];
  /** Reserve type that determines how the reserve was created. */
  reserveType?: ReserveTypeWithLiterals;
  /**
   * ID of the BackOffice activity that triggered creation of the reserve.
   *
   * Available only for 1-time reserves (where `reserveType` is `ONE_TIME`).
   * @internal
   * @format GUID
   */
  creationActivityId?: string | null;
}

export interface BookTransferInfo {
  /**
   * ID of the book transfer that funded this balance record.
   * @format GUID
   */
  bookTransferId?: string;
  /**
   * External reference for the book transfer transaction.
   * @maxLength 100
   */
  transactionReference?: string;
  /** Date and time the funds became instantly available through the book transfer. */
  createdDate?: Date | null;
}

export interface ListBalanceRecordsRequest {
  /**
   * ID of the Wix Payments account that owns the balance records. Retrieve account IDs from the Accounts API.
   * @format GUID
   */
  accountId?: string;
  /**
   * Returns records that belong to the specified Wix Payments account profile.
   * @format GUID
   */
  accountProfileId?: string | null;
  /**
   * One or more tag filter values to apply.
   *
   * Each tag is a named shortcut that the server expands into a composite filter on `recordStatus`, type, `payoutAvailabilityDate`, and `payoutInfo.payoutId`. Tags aren't stored on records. See `BalanceRecordTag` for supported values.
   *
   * When `tags` includes `ESTIMATED_RESERVE_HOLD` or `ESTIMATED_RESERVE_RELEASE`, the first
   * record in the response is virtual: it's calculated on the fly from the current account
   * balance and has no `id` and no `appliedDate`.
   * @maxSize 100
   */
  tags?: BalanceRecordTagWithLiterals[];
  /**
   * Returns records included in the specified payout.
   * @format GUID
   */
  payoutId?: string | null;
  /**
   * Returns records linked to the specified transaction.
   * @format GUID
   */
  transactionId?: string | null;
  /** Returns records whose `paymentInfo.createdDate` is before this date. */
  paymentCreatedBefore?: Date | null;
  /** Returns records whose `paymentInfo.createdDate` is after this date. */
  paymentCreatedAfter?: Date | null;
  /** Returns records whose `appliedDate` is before this date. */
  appliedBefore?: Date | null;
  /** Returns records whose `appliedDate` is after this date. */
  appliedAfter?: Date | null;
  /** Returns records whose `createdDate` is before this date. */
  createdBefore?: Date | null;
  /** Returns records whose `createdDate` is after this date. */
  createdAfter?: Date | null;
  /**
   * Filter by balance record types. Returns records that match any of the listed values.
   * @maxSize 100
   */
  types?: BalanceRecordTypeWithLiterals[];
  /**
   * Sort options for the result list.
   *
   * `fieldName` accepts `applied_date`, `payment_info.created_date`, or `created_date` (snake_case, not auto-converted). At most 1 sort field is allowed.
   *
   * `order` controls direction: `ASC` for ascending (oldest first, since all sortable fields are dates); `DESC` for descending (newest first).
   *
   * Default: `applied_date` in `ASC` order.
   */
  sort?: Sorting;
  /**
   * Paging options.
   *
   * Set `paging.limit` to `0` to retrieve only metadata and summary without records.
   */
  paging?: Paging;
  /**
   * Whether to include the aggregated `summary` in the response.
   *
   * When `true`, totals are computed across the records that matched the filter (not across
   * the whole account) and returned in `summary`.
   *
   * Default: `false`
   */
  includeSummary?: boolean;
  /**
   * Whether to omit the total record count from `metadata.total`.
   *
   * Omitting the total can improve performance on large result sets.
   *
   * Default: `false`
   */
  excludeTotal?: boolean;
}

/**
 * Predefined filter value you can pass to List Balance Records and Query Balance Records.
 *
 * Tags aren't stored on balance records, and you can't apply them yourself. Each tag is a named shortcut that the server expands at query time into a composite filter on `recordStatus`, type, `payoutAvailabilityDate`, and `payoutInfo.payoutId`. You can pass multiple tags in a single request.
 */
export enum BalanceRecordTag {
  /** Unknown balance record tag. */
  UNKNOWN = 'UNKNOWN',
  /** Settled credits and debits not yet paid out (`recordStatus` is `CREDITED` or `DEBITED`). */
  AVAILABLE = 'AVAILABLE',
  /** Credits still in their pending period (`recordStatus` is `PENDING_CREDIT`). */
  PENDING = 'PENDING',
  /** `RESERVE_HOLD` and `RESERVE_RELEASE` records currently at `recordStatus` `RESERVED`. */
  RESERVE = 'RESERVE',
  /**
   * `RESERVE_HOLD` records, with a virtual record (a projection of the next hold) as the first result.
   *
   * The virtual record is calculated on the fly from the current available balance and has no `id` or `appliedDate`.
   */
  ESTIMATED_RESERVE_HOLD = 'ESTIMATED_RESERVE_HOLD',
  /**
   * `RESERVE_RELEASE` records, with a virtual record (a projection of the next release) as the first result.
   *
   * The virtual record is calculated on the fly from the current available balance and has no `id` or `appliedDate`.
   */
  ESTIMATED_RESERVE_RELEASE = 'ESTIMATED_RESERVE_RELEASE',
  /** `RESERVE_RELEASE` records past their `payoutAvailabilityDate`. */
  RESERVE_RELEASED = 'RESERVE_RELEASED',
  /** `TOPUP` records at `recordStatus` `PENDING_CREDIT`. */
  PENDING_TOPUP = 'PENDING_TOPUP',
  /** Any balance record already included in a payout (`recordStatus` is `PAID_OUT` or `DEDUCTED_FROM_PAYOUT`). */
  PAID_OUT = 'PAID_OUT',
  /** Any balance record not yet included in any payout. */
  NOT_PAID_OUT = 'NOT_PAID_OUT',
}

/** @enumType */
export type BalanceRecordTagWithLiterals =
  | BalanceRecordTag
  | 'UNKNOWN'
  | 'AVAILABLE'
  | 'PENDING'
  | 'RESERVE'
  | 'ESTIMATED_RESERVE_HOLD'
  | 'ESTIMATED_RESERVE_RELEASE'
  | 'RESERVE_RELEASED'
  | 'PENDING_TOPUP'
  | 'PAID_OUT'
  | 'NOT_PAID_OUT';

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
  /**
   * When `field_name` is a property of repeated field that is marked as `MATCH_ITEMS` and sort should be done by
   * a specific element from a collection, filter can/should be provided to ensure correct sort value is picked.
   *
   * If multiple filters are provided, they are combined with AND operator.
   *
   * Example:
   * Given we have document like {"id": "1", "nestedField": [{"price": 10, "region": "EU"}, {"price": 20, "region": "US"}]}
   * and `nestedField` is marked as `MATCH_ITEMS`, to ensure that sorting is done by correct region, filter should be
   * { fieldName: "nestedField.price", "select_items_by": [{"nestedField.region": "US"}] }
   * @internal
   * @maxSize 10
   */
  selectItemsBy?: Record<string, any>[] | null;
  /**
   * Origin point for geo-distance sorting on a GEO field
   * results are ordered by distance from this point (ASC = nearest first, DESC = farthest first).
   */
  origin?: AddressLocation;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface AddressLocation {
  /** Address latitude. */
  latitude?: number | null;
  /** Address longitude. */
  longitude?: number | null;
}

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface ListBalanceRecordsResponse {
  /**
   * Retrieved balance records.
   * @maxSize 1000
   */
  records?: BalanceRecord[];
  /** Paging metadata for the response. */
  metadata?: PagingMetadata;
  /**
   * Aggregated summary across the records that matched the filter.
   *
   * Returned only when `includeSummary` is `true`.
   */
  summary?: BalanceRecordsSummary;
}

export interface PagingMetadata {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
  /**
   * Indicates if there are more results after the current page.
   * If `true`, another page of results can be retrieved.
   * If `false`, this is the last page.
   * @internal
   */
  hasNext?: boolean | null;
}

/**
 * Aggregated summary of balance records, grouped by category.
 *
 * Returned in List Balance Records and Query Balance Records responses when `includeSummary`
 * is `true`. The category-level breakdown is in `items`. The `total*` fields aggregate across
 * all matched records.
 */
export interface BalanceRecordsSummary {
  /** Net total across all matched records. */
  total?: Money;
  /** Gross total across all matched records, before fees. */
  totalAmount?: Money;
  /** Total fees across all matched records. */
  totalFees?: Money;
  /** Total net amount across all matched records, equivalent to `total`. */
  totalNet?: Money;
  /**
   * Per-category breakdown of the aggregated records.
   * @maxSize 100
   */
  items?: BalanceRecordsSummaryItem[];
}

export interface BalanceRecordsSummaryItem {
  /**
   * Category of records aggregated by this item.
   *
   * Deprecated. Use `summaryItemType` instead.
   * @deprecated Category of records aggregated by this item.
   *
   * Deprecated. Use `summaryItemType` instead.
   * @replacedBy balance_records_summary_item.summary_item_type
   * @targetRemovalDate 2027-05-31
   */
  summaryType?: BalanceRecordsSummaryItemTypeWithLiterals;
  /** Category of records aggregated by this item. */
  summaryItemType?: BalanceRecordsSummaryItemTypeWithLiterals;
  /** Gross amount for this category, before fees. */
  amount?: Money;
  /** Total fees for this category. */
  fees?: Money;
  /** Net amount for this category, after fees. */
  net?: Money;
}

/** Category used to group balance records in a summary. */
export enum BalanceRecordsSummaryItemType {
  /** Unknown summary item type. */
  UNKNOWN = 'UNKNOWN',
  /** All payments from buyers to the account. */
  CREDITS = 'CREDITS',
  /** All refunds issued to buyers. */
  REFUNDS = 'REFUNDS',
  /** All chargebacks and chargeback fees. */
  CHARGEBACKS = 'CHARGEBACKS',
  /** All reserve holds. */
  RESERVE_HOLDS = 'RESERVE_HOLDS',
  /** All reserve releases. */
  RESERVE_RELEASES = 'RESERVE_RELEASES',
  /** All top-ups into the account. */
  FUNDS_ADDED = 'FUNDS_ADDED',
  /** All transfers between accounts. */
  FUNDS_TRANSFERRED = 'FUNDS_TRANSFERRED',
  /** All Wix Capital movements, including cash advances, automatic remittances, and adjustments. */
  CAPITAL = 'CAPITAL',
}

/** @enumType */
export type BalanceRecordsSummaryItemTypeWithLiterals =
  | BalanceRecordsSummaryItemType
  | 'UNKNOWN'
  | 'CREDITS'
  | 'REFUNDS'
  | 'CHARGEBACKS'
  | 'RESERVE_HOLDS'
  | 'RESERVE_RELEASES'
  | 'FUNDS_ADDED'
  | 'FUNDS_TRANSFERRED'
  | 'CAPITAL';

export interface GetBalanceRecordRequest {
  /**
   * ID of the Wix Payments account that owns the balance record. The record must belong to this account. Retrieve account IDs from the Accounts API.
   * @format GUID
   */
  accountId: string;
  /**
   * Balance record ID.
   * @format GUID
   */
  balanceRecordId: string;
}

export interface GetBalanceRecordResponse {
  /** Retrieved balance record. */
  record?: BalanceRecord;
}

export interface QueryBalanceRecordsRequest {
  /**
   * ID of the Wix Payments account that owns the balance records. Retrieve account IDs from the Accounts API.
   * @format GUID
   */
  accountId: string;
  /**
   * Query object that supports filtering, sorting, and paging.
   *
   * Filter and sort field names must be **snake_case**. They're read from the `filter`
   * object and aren't auto-converted. Supported filter fields: `account_profile_id`,
   * `tags`, `payout_id`, `transaction_id`, `types`, and the date ranges
   * `transaction_creation_date`, `paid`, and `created_date`. Any other field returns
   * an `Unsupported query: unknown parameter <field>` error. ID fields accept a single
   * value by equality, `tags` and `types` accept `$in`, and date-range fields accept `$gte`
   * and `$lt`. Supported `sort[i].fieldName` values: `applied_date`,
   * `payment_info.created_date`, `created_date`. At most 1 sort field is allowed.
   */
  query?: Query;
  /**
   * Whether to include the aggregated `summary` in the response.
   *
   * When `true`, totals are computed across the records that matched the query (not across
   * the whole account) and returned in `summary`.
   *
   * Default: `false`
   */
  includeSummary?: boolean;
  /**
   * Whether to omit the total record count from `metadata.total`.
   *
   * Omitting the total can improve performance on large result sets.
   *
   * Default: `false`
   */
  excludeTotal?: boolean;
}

export interface Query {
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`, `$contains`
   */
  filter?: any;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   */
  sort?: Sorting[];
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
  /** Array of projected fields. A list of specific field names to return. If `fieldsets` are also specified, the union of `fieldsets` and `fields` is returned. */
  fields?: string[];
  /** Array of named, predefined sets of projected fields. A array of predefined named sets of fields to be returned. Specifying multiple `fieldsets` will return the union of fields from all sets. If `fields` are also specified, the union of `fieldsets` and `fields` is returned. */
  fieldsets?: string[];
}

export interface QueryBalanceRecordsResponse {
  /**
   * Retrieved balance records.
   * @maxSize 1000
   */
  records?: BalanceRecord[];
  /** Paging metadata for the response. */
  metadata?: PagingMetadata;
  /**
   * Aggregated summary across the records that matched the query.
   *
   * Returned only when `includeSummary` is `true`.
   */
  summary?: BalanceRecordsSummary;
}

export interface GetBalanceSummaryRequest {
  /**
   * ID of the Wix Payments account to summarize. Retrieve account IDs from the Accounts API.
   * @format GUID
   */
  accountId?: string;
  /**
   * Field mask of summary categories to include in the response.
   *
   * Supported paths: `scheduled`, `processing`, `funds_on_hold`, `payouts_being_verified`,
   * `payouts_under_minimum_amount`, `negative`, `reserve`, `blocked`, `modal`. Any other
   * path is rejected.
   *
   * When omitted, all applicable categories are returned. When sent with one or more paths,
   * only the listed categories are populated. When sent as an empty mask, no categories are
   * populated.
   */
  fields?: string[];
}

export interface GetBalanceSummaryResponse {
  /**
   * Retrieved balance summary.
   *
   * Only the categories requested through `fields` are populated.
   */
  summary?: BalanceSummary;
}

/**
 * Aggregated balance summary for a Wix Payments account.
 *
 * Each top-level field represents a balance category. Categories are populated based on the
 * `fields` mask sent in the Get Balance Summary request, and on the account's current state:
 * categories are returned conditionally so the set returned is itself a signal about the
 * account.
 */
export interface BalanceSummary {
  /** Funds scheduled for an upcoming payout, with profile-level breakdown. */
  scheduled?: ScheduledBalance;
  /** Funds currently being processed and not yet available, with profile-level breakdown. */
  processing?: ProcessingBalance;
  /** Funds on hold that can't currently move to a payout, for example because of a failed payout, with profile-level breakdown. */
  fundsOnHold?: FundsOnHoldBalance;
  /** Funds held in payouts that are undergoing verification before they can be released, with profile-level breakdown. */
  payoutsBeingVerified?: PayoutsBeingVerifiedBalance;
  /** Funds that are ready but haven't reached the minimum payout amount, with profile-level breakdown. */
  payoutsUnderMinimumAmount?: PayoutsUnderMinimumAmountBalance;
  /** Negative balance owed by the merchant, with profile-level breakdown and suggested top-up amounts. */
  negative?: NegativeBalance;
  /** Funds held in reserve, with profile-level breakdown and reserve motivation. */
  reserve?: ReserveBalance;
  /** Funds blocked from payout by an internal operational hold, with profile-level breakdown. */
  blocked?: BlockedBalance;
  /** Additional account-level data used to render reserve and pending-period explanatory views in client UIs. */
  modal?: ModalData;
}

export interface ScheduledBalance {
  /** Total amount scheduled for the next payout across all profiles. */
  amount?: Money;
  /**
   * Per-profile breakdown of the scheduled balance.
   * @maxSize 100
   */
  profiles?: ScheduledProfileBalance[];
}

export interface ScheduledProfileBalance {
  /**
   * ID of the Wix Payments account profile.
   * @format GUID
   */
  accountProfileId?: string;
  /** Amount scheduled for the next payout for this profile. */
  amount?: Money;
  /**
   * Tags associated with the records aggregated into this balance.
   * @maxSize 100
   */
  tags?: BalanceRecordTagWithLiterals[];
  /**
   * Date the payout is scheduled to be sent, in `YYYY-MM-DD` format.
   * @format LOCAL_DATE
   */
  scheduledDate?: string | null;
  /** Whether the amount includes a Wix Capital cash advance component. */
  cashAdvanceIncluded?: boolean;
}

export interface ProcessingBalance {
  /** Total amount currently being processed across all profiles. */
  amount?: Money;
  /**
   * Per-profile breakdown of the processing balance.
   * @maxSize 100
   */
  profiles?: ProcessingProfileBalance[];
  /** Start of the date range covered by the processing aggregation. */
  aggregatedFromDate?: Date | null;
  /** End of the date range covered by the processing aggregation. */
  aggregatedToDate?: Date | null;
  /** Length of the pending period during which each payment waits before becoming available. */
  paymentPendingPeriod?: Period;
}

export interface ProcessingProfileBalance {
  /**
   * ID of the Wix Payments account profile.
   * @format GUID
   */
  accountProfileId?: string;
  /** Amount currently being processed for this profile. */
  amount?: Money;
  /**
   * Tags associated with the records aggregated into this balance.
   * @maxSize 100
   */
  tags?: BalanceRecordTagWithLiterals[];
  /** Whether the amount includes a Wix Capital cash advance component. */
  cashAdvanceIncluded?: boolean;
}

export interface Period {
  /** The number of units */
  value?: number;
  /** The unit type */
  unit?: PeriodUnitWithLiterals;
}

export enum PeriodUnit {
  UNKNOWN = 'UNKNOWN',
  DAY = 'DAY',
  BUSINESS_DAY = 'BUSINESS_DAY',
}

/** @enumType */
export type PeriodUnitWithLiterals =
  | PeriodUnit
  | 'UNKNOWN'
  | 'DAY'
  | 'BUSINESS_DAY';

export interface FundsOnHoldBalance {
  /** Total amount on hold across all profiles. */
  amount?: Money;
  /**
   * Per-profile breakdown of funds on hold.
   * @maxSize 100
   */
  profiles?: FundsOnHoldProfileBalance[];
}

export interface FundsOnHoldProfileBalance {
  /**
   * ID of the Wix Payments account profile.
   * @format GUID
   */
  accountProfileId?: string;
  /** Amount on hold for this profile. */
  amount?: Money;
  /**
   * Tags associated with the records aggregated into this balance.
   * @maxSize 100
   */
  tags?: BalanceRecordTagWithLiterals[];
  /**
   * ID of the most recent payout that failed for the current bank details and caused funds to be placed on hold.
   * @format GUID
   */
  problematicPayoutId?: string | null;
  /** Whether the amount includes a Wix Capital cash advance component. */
  cashAdvanceIncluded?: boolean;
}

export interface PayoutsBeingVerifiedBalance {
  /** Total amount held in payouts being verified across all profiles. */
  amount?: Money;
  /**
   * Per-profile breakdown of payouts being verified.
   * @maxSize 100
   */
  profiles?: PayoutsBeingVerifiedProfileBalance[];
}

export interface PayoutsBeingVerifiedProfileBalance {
  /**
   * ID of the Wix Payments account profile.
   * @format GUID
   */
  accountProfileId?: string;
  /** Amount being verified for this profile. */
  amount?: Money;
  /**
   * Tags associated with the records aggregated into this balance.
   * @maxSize 100
   */
  tags?: BalanceRecordTagWithLiterals[];
  /** Whether the amount includes a Wix Capital cash advance component. */
  cashAdvanceIncluded?: boolean;
}

export interface PayoutsUnderMinimumAmountBalance {
  /** Total amount below the minimum payout threshold across all profiles. */
  amount?: Money;
  /**
   * Per-profile breakdown of balances that are below the minimum payout threshold.
   * @maxSize 100
   */
  profiles?: PayoutsUnderMinimumAmountProfileBalance[];
  /** Minimum payout amount configured for the account. */
  minPayoutAmount?: Money;
}

export interface PayoutsUnderMinimumAmountProfileBalance {
  /**
   * ID of the Wix Payments account profile.
   * @format GUID
   */
  accountProfileId?: string;
  /** Amount available for this profile that doesn't yet meet the minimum payout threshold. */
  amount?: Money;
  /**
   * Tags associated with the records aggregated into this balance.
   * @maxSize 100
   */
  tags?: BalanceRecordTagWithLiterals[];
  /** Whether the amount includes a Wix Capital cash advance component. */
  cashAdvanceIncluded?: boolean;
}

export interface NegativeBalance {
  /** Total negative balance owed across all profiles. Returned as a negative or zero amount. */
  amount?: Money;
  /**
   * Per-profile breakdown of the negative balance.
   * @maxSize 100
   */
  profiles?: NegativeProfileBalance[];
  /** Suggested top-up amount that would clear the negative balance. */
  suggestedTopupAmount?: Money;
}

export interface NegativeProfileBalance {
  /**
   * ID of the Wix Payments account profile.
   * @format GUID
   */
  accountProfileId?: string;
  /** Negative amount owed for this profile. */
  amount?: Money;
  /**
   * Tags associated with the records aggregated into this balance.
   * @maxSize 100
   */
  tags?: BalanceRecordTagWithLiterals[];
  /** Smallest top-up amount that the merchant is allowed to submit for this profile. */
  minTopupAmount?: Money;
  /** Largest top-up amount that the merchant is allowed to submit for this profile. */
  maxTopupAmount?: Money;
  /** Recommended top-up amount for this profile that brings the balance non-negative. */
  suggestedTopupAmount?: Money;
  /** Whether the amount includes a Wix Capital cash advance component. */
  cashAdvanceIncluded?: boolean;
}

export interface ReserveBalance extends ReserveBalanceMotivationOneOf {
  /** Reserve policy that automatically generates the reserve. */
  reservePolicy?: ReservePolicy;
  /** Reserve that was created explicitly for a 1-time event. */
  explicitReserve?: ExplicitReserve;
  /** Total amount currently held in reserve across all profiles. */
  amount?: Money;
  /**
   * Per-profile breakdown of the reserve balance.
   * @maxSize 100
   */
  profiles?: ReserveProfileBalance[];
}

/** @oneof */
export interface ReserveBalanceMotivationOneOf {
  /** Reserve policy that automatically generates the reserve. */
  reservePolicy?: ReservePolicy;
  /** Reserve that was created explicitly for a 1-time event. */
  explicitReserve?: ExplicitReserve;
}

export interface ReserveProfileBalance {
  /**
   * ID of the Wix Payments account profile.
   * @format GUID
   */
  accountProfileId?: string;
  /** Amount currently held in reserve for this profile. */
  amount?: Money;
  /**
   * Tags associated with the records aggregated into this balance.
   * @maxSize 100
   */
  tags?: BalanceRecordTagWithLiterals[];
}

export interface ReservePolicy {
  /**
   * Percentage of the available balance that's held in reserve.
   * @min 1
   * @max 99
   */
  percentage?: number;
  /** Length of time each portion of the reserve is held before it's released. */
  period?: Period;
}

/** Indicates a reserve that was created explicitly for a specific event, with no further configuration. */
export interface ExplicitReserve {}

export interface BlockedBalance {
  /** Total amount blocked from payout across all profiles. */
  amount?: Money;
  /**
   * Per-profile breakdown of the blocked balance.
   * @maxSize 100
   */
  profiles?: BlockedProfileBalance[];
}

export interface BlockedProfileBalance {
  /**
   * ID of the Wix Payments account profile.
   * @format GUID
   */
  accountProfileId?: string;
  /** Amount blocked from payout for this profile. */
  amount?: Money;
  /**
   * Tags associated with the records aggregated into this balance.
   * @maxSize 100
   */
  tags?: BalanceRecordTagWithLiterals[];
  /** Whether the amount includes a Wix Capital cash advance component. */
  cashAdvanceIncluded?: boolean;
}

/** Account-level metadata used by clients to render reserve and pending-period explanatory views. */
export interface ModalData {
  /** Merchant's active reserve policy, if any. */
  reservePolicy?: ReservePolicy;
  /** Merchant's configured pending period. */
  paymentPendingPeriod?: Period;
}
